import React, { useEffect, useMemo, useState } from 'react';

interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const featureCards: Array<{
  title: string;
  description: string;
  points: string[];
  gradient: string;
  icon: React.ReactNode;
}> = [
  {
    title: 'Premium Mockup Library',
    description: 'Choose from launch-tested templates for software, print, apparel, packaging, and more.',
    points: ['50+ production-ready scenes', 'Light & dark backgrounds', 'HD exports in seconds'],
    gradient: 'from-indigo-500/10 via-sky-400/10 to-indigo-500/5',
    icon: (
      <svg className="w-7 h-7 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 6a2 2 0 012-2h6m-8 4h8M4 14h16m-7-8h5a2 2 0 012 2v10a2 2 0 01-2 2h-5m-6 0h6m-6 0H6a2 2 0 01-2-2V8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    title: 'Brand Controls',
    description: 'Lock in typography, color palettes, and logos to keep every deliverable on-brand.',
    points: ['Upload once, reuse everywhere', 'Agency client folders', 'Pixel-perfect alignment'],
    gradient: 'from-amber-500/10 via-orange-400/10 to-amber-500/5',
    icon: (
      <svg className="w-7 h-7 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3l4 4-6.5 6.5a2 2 0 00-.5.9l-.9 3.1a.5.5 0 00.6.6l3.1-.9a2 2 0 00.9-.5L21 8l4 4M16 7l-1.5-1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 21a2 2 0 01-2-2v-2.5a2 2 0 01.6-1.4l8.6-8.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    title: 'AI Enhancements',
    description: 'Bring any sketch to life with Gemini-powered lighting, reflections, and background removal.',
    points: ['SceneScaper AI for bespoke scenes', 'One-tap background removal', 'Gloss, shadow & depth controls'],
    gradient: 'from-emerald-500/10 via-teal-400/10 to-emerald-500/5',
    icon: (
      <svg className="w-7 h-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3l1.9 5.9H20l-4.9 3.6 1.9 5.9L12 14.8 7 18.4 8.9 12.5 4 8.9h6.1L12 3z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
];

const workflowSteps = [
  {
    title: 'Upload in seconds',
    description: 'Drop in any logo or product image. Automatic file handling keeps originals intact.',
    icon: (
      <svg className="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 9l5-5 5 5M12 4v12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    title: 'Select a template',
    description: 'Pick the funnel cover, packaging, or device mockup that fits the campaign, then tailor colors instantly.',
    icon: (
      <svg className="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    title: 'Polish & export',
    description: 'Generate with Gemini or manual controls, then export layered PNGs ready for launch decks.',
    icon: (
      <svg className="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 17a4 4 0 014-4h8a4 4 0 010 8H8a4 4 0 01-4-4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 5l3-3 3 3M12 2v12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
];

const showcaseItems = [
  {
    title: 'Product launch suite',
    description: 'Course boxes, membership dashboards, webinar decks, and hero shots.',
    image: '/mockup-0a3a2bb6-bc1e-4099-9848-705993c6d6ae-product-box-large.png'
  },
  {
    title: 'Premium apparel drops',
    description: 'Lifestyle t-shirts, hoodies, and merchandising composites.',
    image: '/mockup-1f640a41-8525-46f7-a433-e72fc957c7b0-tshirt-front.png'
  },
  {
    title: 'SceneScaper visuals',
    description: 'AI-generated brand environments with perfect lighting and negative prompts.',
    image: '/mockup-39636342-18fc-4a69-afbb-47a7362a9b77-laptop-screen.png'
  }
];

const pricingTiers = [
  {
    name: 'Professional',
    price: '$97',
    description: 'For founders and marketers shipping weekly campaigns.',
    features: [
      'Full template library & updates',
      'Unlimited exports & background removal',
      'SceneScaper AI credits: 40 / month',
      'Commercial license + brand kit storage'
    ],
    highlighted: true
  },
  {
    name: 'Agency',
    price: '$197',
    description: 'Built for agencies managing client launches at scale.',
    features: [
      'Everything in Professional',
      'Client workspaces & approvals',
      'Team seats with granular permissions',
      'JVZoo licensing + priority support'
    ],
    highlighted: false
  }
];

const testimonials = [
  {
    name: 'Alicia Bennett',
    role: 'Creative Director, Launch Labs',
    quote:
      'We replaced three separate Photoshop requests a day with BrandScapes AI. Clients sign off on visuals in minutes.',
    avatar: ''
  },
  {
    name: 'Marcus Chen',
    role: 'Founder, FunnelWorks',
    quote:
      'SceneScaper nailed the exact lifestyle setting for our latest SaaS roll-out. The credit system keeps budgets predictable.',
    avatar: ''
  },
  {
    name: 'Priya Desai',
    role: 'Brand Strategist, Bold & Beyond',
    quote:
      'The brand kit and device templates are a dream for presenting mood boards. Export quality is pitch-deck ready.',
    avatar: ''
  }
];

const faqs = [
  {
    question: 'How fast can I generate a mockup?',
    answer:
      'Most users create a finished mockup in under 60 seconds. Upload, choose a template, refine prompts, and export instantly.'
  },
  {
    question: 'What image formats can I upload?',
    answer:
      'PNG, JPG, SVG, and WebP files up to 10MB are supported. We keep originals intact so you can rework them later.'
  },
  {
    question: 'Does the license cover client work?',
    answer:
      'Yes. Both Professional and Agency plans include commercial usage. Agency unlocks client folders and approval flows.'
  },
  {
    question: 'Do I need design experience?',
    answer:
      'No. The workflow, prompts, and brand presets are built for marketers. Designers love the speedups too.'
  }
];

const trustMetrics = [
  { label: 'Marketing teams on board', value: '10,000+' },
  { label: 'Mockups produced last month', value: '187,000' },
  { label: 'Average rating', value: '4.9 / 5' }
];

const logos = ['ConvertFlow', 'LaunchLab', 'FunnelWorks', 'Spark Digital', 'Nova Commerce'];

const Special: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<CountdownState>({
    days: 0,
    hours: 22,
    minutes: 15,
    seconds: 32
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        }
        if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        }
        if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formattedCountdown = useMemo(() => [
    { label: 'Days', value: String(timeLeft.days).padStart(2, '0') },
    { label: 'Hours', value: String(timeLeft.hours).padStart(2, '0') },
    { label: 'Minutes', value: String(timeLeft.minutes).padStart(2, '0') },
    { label: 'Seconds', value: String(timeLeft.seconds).padStart(2, '0') }
  ], [timeLeft]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900">
      <header className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-slate-100">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-r from-indigo-100 via-sky-100 to-indigo-100 opacity-60" />
        <div className="absolute -right-24 -top-24 w-72 h-72 bg-sky-200 rounded-full blur-3xl opacity-50" />
        <div className="absolute -left-24 top-36 w-72 h-72 bg-indigo-200 rounded-full blur-3xl opacity-40" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pb-28">
          <div className="flex flex-col lg:flex-row items-center gap-14">
            <div className="w-full lg:w-1/2">
              <div className="mb-6 flex items-center gap-3">
                <img src="/clawbrand_logo_color.png" alt="ClawBrand AI" className="h-10" />
                <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-sm font-medium text-slate-600 shadow-sm">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Trusted by growth teams worldwide
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-slate-900">
                Produce agency-grade mockups in under a minute.
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl">
                BrandScapes AI turns any logo, product, or course into polished visuals ready for funnels, ads, and investor decks. No Photoshop. No revisions. Just instant, on-brand imagery.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                >
                  Claim launch offer
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-8 py-4 text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-100"
                >
                  View templates
                </button>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-6 text-sm text-slate-500">
                {trustMetrics.map(metric => (
                  <div key={metric.label} className="rounded-xl border border-white/50 bg-white/80 p-4 shadow-sm">
                    <div className="text-xl font-semibold text-slate-900">{metric.value}</div>
                    <div>{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="relative rounded-3xl bg-white p-6 shadow-2xl shadow-indigo-100">
                <div className="absolute -top-10 right-6 inline-flex rounded-full bg-emerald-50 px-5 py-2 text-sm font-semibold text-emerald-600 shadow-md">
                  Live launch bonus ends soon
                </div>
                <div className="aspect-[5/4] overflow-hidden rounded-2xl bg-slate-100">
                  <img
                    src="/box-laptop-tablet-phone.png"
                    alt="BrandScapes AI mockup montage"
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-600">Launch pricing ends in</div>
                    <div className="grid grid-cols-4 gap-3 text-center text-slate-900">
                      {formattedCountdown.map(item => (
                        <div key={item.label} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                          <div className="text-lg font-semibold">{item.value}</div>
                          <div className="text-[11px] uppercase tracking-wide text-slate-400">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-xs text-slate-400">No recurring fees • Commercial license included • Secure checkout</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="border-y border-slate-200 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-slate-400">
            {logos.map(name => (
              <span key={name} className="rounded-full border border-slate-100 bg-slate-50 px-4 py-2 shadow-sm">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-semibold text-slate-900">Everything you need to ship beautiful, on-brand creative.</h2>
            <p className="mt-4 text-lg text-slate-600">From entry point offer to full launch stack, BrandScapes AI handles every visual asset without compromising quality.</p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {featureCards.map(card => (
              <div key={card.title} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/30 transition hover:-translate-y-1 hover:shadow-xl">
                <div className={`inline-flex items-center justify-center rounded-2xl bg-gradient-to-br ${card.gradient} p-3 shadow-inner shadow-white`}>{card.icon}</div>
                <h3 className="mt-6 text-xl font-semibold text-slate-900">{card.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{card.description}</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  {card.points.map(point => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-indigo-400" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h2 className="text-3xl font-semibold text-slate-900">A workflow designed for speed and control.</h2>
              <p className="mt-4 text-lg text-slate-600">Every step—from upload to export—was crafted alongside growth teams to keep campaigns on schedule and completely on-brand.</p>

              <div className="mt-10 space-y-6">
                {workflowSteps.map(step => (
                  <div key={step.title} className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
                    <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-white shadow-sm">
                      {step.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 via-white to-slate-100 p-6 shadow-xl">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-indigo-500">Insider tip</h3>
              <p className="mt-4 text-lg text-slate-700">
                Agencies use BrandScapes AI to align launch creative with brand guidelines before designers touch a single PSD. The built-in brand kit keeps client assets locked and ready for every campaign.
              </p>
              <div className="mt-6 rounded-2xl bg-white p-5 text-sm text-slate-600 shadow-inner shadow-slate-200">
                <p className="font-medium text-slate-900">Pro workflow</p>
                <ol className="mt-3 list-decimal space-y-2 pl-4">
                  <li>Save your logo & color palette in the brand kit.</li>
                  <li>Pick a template, apply brand accents, and preview variants.</li>
                  <li>Generate with Gemini, toggle reflections or glossy finish, then export layered PNGs.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="text-3xl font-semibold text-slate-900">Mockups that sell the vision before a single revision.</h2>
              <p className="mt-3 text-lg text-slate-600">Hand-picked templates prove how your offer looks in the real world.</p>
            </div>
            <button
              onClick={() => scrollToSection('pricing')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
            >
              Explore plans
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {showcaseItems.map(item => (
              <div key={item.title} className="group rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/30 transition hover:-translate-y-1 hover:shadow-xl">
                <div className="relative overflow-hidden rounded-t-3xl bg-slate-100">
                  <img src={item.image} alt={item.title} className="h-60 w-full object-cover transition duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-center mx-auto">
            <h2 className="text-3xl font-semibold text-slate-900">Launch pricing. Lifetime value.</h2>
            <p className="mt-4 text-lg text-slate-600">Secure the plan that matches your growth goals. One-time payment. Lifetime access. No hidden upsells.</p>
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {pricingTiers.map(tier => (
              <div
                key={tier.name}
                className={`flex h-full flex-col rounded-3xl border ${tier.highlighted ? 'border-indigo-300 bg-gradient-to-br from-white via-indigo-50 to-white shadow-xl shadow-indigo-100/60' : 'border-slate-200 bg-white shadow-lg shadow-slate-200/40'} p-8`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-slate-900">{tier.name}</h3>
                  {tier.highlighted && (
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                      Most popular
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-slate-600">{tier.description}</p>
                <div className="mt-6 text-4xl font-semibold text-slate-900">{tier.price}</div>
                <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">One-time payment</p>

                <ul className="mt-6 space-y-3 text-sm text-slate-700">
                  {tier.features.map(feature => (
                    <li key={feature} className="flex items-start gap-2">
                      <span className="mt-1 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-100">
                        <svg className="h-3 w-3 text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4.5 4.5a1 1 0 001.414 0l7.5-7.5z" />
                        </svg>
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => scrollToSection('cta')}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${tier.highlighted ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700 focus:ring-indigo-400' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 focus:ring-slate-200'}`}
                >
                  Get {tier.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 md:grid-cols-3">
            {testimonials.map(testimonial => (
              <div key={testimonial.name} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/30">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
                    {testimonial.avatar ? (
                      <img src={testimonial.avatar} alt={testimonial.name} className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      testimonial.name
                        .split(' ')
                        .map(part => part[0])
                        .slice(0, 2)
                        .join('')
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm text-slate-600">“{testimonial.quote}”</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-semibold text-slate-900">Frequently asked questions</h2>
            <p className="mt-3 text-lg text-slate-600">Everything you need to know before your first campaign.</p>
          </div>

          <dl className="mt-10 space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFAQ === index;
              return (
                <div key={faq.question} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                  <dt>
                    <button
                      onClick={() => setOpenFAQ(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-6 py-4 text-left text-sm font-medium text-slate-900"
                    >
                      {faq.question}
                      <span className="ml-4 flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                  </dt>
                  {isOpen && (
                    <dd className="px-6 pb-5 text-sm text-slate-600">{faq.answer}</dd>
                  )}
                </div>
              );
            })}
          </dl>
        </div>
      </section>

      <section id="cta" className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-indigo-500 to-sky-500 py-20 text-white">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <h2 className="text-3xl font-semibold tracking-tight">Build launch visuals that close clients and convert visitors.</h2>
            <p className="mt-4 max-w-2xl text-lg text-indigo-100">
              Your license includes every template, SceneScaper credits, and lifetime updates. Start today and deliver your next launch in record time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => scrollToSection('pricing')}
                className="inline-flex items-center justify-center rounded-full bg-white px-8 py-4 text-base font-semibold text-indigo-600 shadow-lg shadow-indigo-900/20 transition hover:bg-indigo-50"
              >
                Secure your lifetime access
              </button>
              <a
                href="mailto:hello@brandscapes.ai"
                className="text-sm font-semibold text-white/80 underline-offset-4 hover:text-white hover:underline"
              >
                Talk to sales
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-wide text-indigo-100">Encrypted checkout • 30-day guarantee • Instant activation</p>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 py-12 text-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between text-sm">
            <div className="flex items-center gap-3">
              <img src="/clawbrand_logo_color.png" alt="ClawBrand AI" className="h-8" />
              <span className="text-slate-400">© {new Date().getFullYear()} BrandScapes AI. All rights reserved.</span>
            </div>
            <div className="flex flex-wrap gap-4 text-slate-400">
              <a href="/privacy" className="hover:text-white">Privacy</a>
              <a href="/terms" className="hover:text-white">Terms</a>
              <a href="/contact" className="hover:text-white">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Special;
